// 📄 src/components/InstallationServices.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Wrench, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

const InstallationServices = () => {
  return (
    <section
      id="installation-services"
      className="relative bg-black py-16"
    >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 🧱 Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4 drop-shadow">
            Expert Installation at Our{' '}
            <span className="text-brandred">Chula Vista Shop</span>
          </h2>
          <p className="text-lg text-gray-200 max-w-3xl mx-auto">
            We don't just sell parts — we install them. Visit our Chula Vista shop and get your Tacoma
            upgraded by the experts. From dash mounts to lift kits, we handle it all.
          </p>
        </motion.div>

        {/* 🧰 Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              {/* Card 1 */}
              <div 
                className="rounded-2xl p-6 flex gap-4 items-start shadow-lg"
                style={{ backgroundColor: '#ffffff' }}
              >
                <div className="w-12 h-12 bg-brandred/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Wrench className="text-brandred" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-black mb-2">
                    Professional Installation
                  </h3>
                  <p className="text-gray-700">
                    Our certified technicians specialize in Toyota trucks and ensure every
                    installation meets factory standards.
                  </p>
                </div>
              </div>

              {/* Card 2 */}
              <div 
                className="rounded-2xl p-6 flex gap-4 items-start shadow-lg"
                style={{ backgroundColor: '#ffffff' }}
              >
                <div className="w-12 h-12 bg-brandred/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="text-brandred" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-black mb-2">
                    Same-Day Service
                  </h3>
                  <p className="text-gray-700">
                    Most installations can be completed the same day. Book your appointment and get
                    back on the road faster.
                  </p>
                </div>
              </div>
            </div>

            {/* Call-to-action */}
            <Link
              to="/contact?install=true"
              className="inline-flex items-center gap-2 bg-brandred text-offwhite font-bold text-lg px-8 py-4 rounded-lg shadow-lg hover:bg-gradient-to-r hover:from-brandred hover:to-[#E63B3B] transition-all duration-300"
            >
              <Calendar size={20} />
              Book an Install
            </Link>
          </motion.div>

          {/* Right side - Address */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="rounded-2xl p-8 shadow-lg"
            style={{ backgroundColor: '#ffffff' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-brandred/10 rounded-lg flex items-center justify-center">
                <MapPin className="text-brandred" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-black">Visit Our Shop</h3>
            </div>

            <div className="space-y-4">
              <div>
                <p className="font-semibold text-black mb-1">Taco Garage</p>
                <p className="text-gray-700">1409 Broadway</p>
                <p className="text-gray-700">Suite 101</p>
                <p className="text-gray-700">Chula Vista, CA 91911</p>
              </div>

              {/* Map */}
              <div 
                onClick={() => window.open('https://maps.google.com/?q=1409+Broadway,+Suite+101,+Chula+Vista,+CA+91911', '_blank')}
                className="cursor-pointer rounded-xl overflow-hidden border border-gray-700/40 shadow-md hover:shadow-lg transition-shadow duration-300 mt-4 relative map-container"
              >
                <iframe
                  title="Taco Garage Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13498.09287937004!2d-117.0791592322975!3d32.60102525100762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80d94f51c9e3297b%3A0xb29c6943c77f9625!2sTaco%20Garage!5e0!3m2!1sen!2sus!4v1690927278005!5m2!1sen!2sus"
                  width="100%"
                  height="200"
                  className="w-full"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>

              <div className="pt-4 border-t border-gray-300">
                <p className="text-sm text-gray-700">
                  <strong className="text-black">Hours:</strong> Tuesday - Saturday 10am to 5pm
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InstallationServices;
